import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { BACKEND_URL } from "@/lib/api";
import CollabPicker from "@/components/CollabPicker";
import { toast } from "sonner";

const GENRES = [
  "Hip Hop",
  "Rap",
  "Trap",
  "R&B",
  "Pop",
  "Afrobeats",
  "Dancehall",
  "Reggae",
  "Drill",
  "Alternative",
  "Other"
];

const TrackEditPage = () => {
  const { submission_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioSavedFlash, setAudioSavedFlash] = useState(false);
  const [track, setTrack] = useState(null);
  const [formData, setFormData] = useState({
    track_title: "",
    description: "",
    genre: "",
    tags: [],
    featured_artists: [], // Phase 3.2: collaborator tags
    // Phase 9.2 — fix-without-reupload polish
    mood: [],
    credits: "",
    artist_name_display: "",
    // M1.5 — artist opt-in for Beam Live rebroadcast. Hydrated from
    // the fetched submission; default `false` for existing tracks.
    live_rebroadcast_ok: false,
  });
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverSavedFlash, setCoverSavedFlash] = useState(false); // Phase 3.2: success state
  const [tagInput, setTagInput] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [savingLiveRebroadcast, setSavingLiveRebroadcast] = useState(false);
  // Beam Music feature flag (Option A). Fetched from backend runtime
  // config so a build-time React env doesn't dictate visibility.
  // Fail-closed: default OFF. When OFF, the consent card is not shown.
  const [beamMusicEnabled, setBeamMusicEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    // Backend returns {enabled, admin_only, accessible}. `accessible`
    // folds in the admin-only gate so this page only renders the
    // consent toggle for users who can actually use Beam Music.
    api.get("/config/beam-music")
      .then((r) => { if (!cancelled) setBeamMusicEnabled(!!r?.data?.accessible); })
      .catch(() => { if (!cancelled) setBeamMusicEnabled(false); });
    return () => { cancelled = true; };
  }, []);
  const coverRef = useRef(null);

  const fetchTrack = useCallback(async () => {
    try {
      // First check if user can edit this track
      const canEditResponse = await api.get(`/track/${submission_id}/can-edit`);
      if (!canEditResponse.data.can_edit) {
        toast.error("You can only edit your own tracks");
        navigate(`/track/${submission_id}`);
        return;
      }

      // Fetch track details
      const response = await api.get(`/track/${submission_id}`);
      const trackData = response.data.submission;
      setTrack(trackData);
      
      setFormData({
        track_title: trackData.track_title || "",
        description: trackData.description || "",
        genre: trackData.genre || "",
        tags: trackData.tags || [],
        featured_artists: trackData.featured_artists || [],
        mood: trackData.mood || [],
        credits: trackData.credits || "",
        artist_name_display: trackData.artist_name_display || "",
        live_rebroadcast_ok: trackData.live_rebroadcast_ok === true,
      });
      setCurrentUserId(trackData.user_id);

      if (trackData.cover_url) {
        setCoverPreview(trackData.cover_url);
      }
    } catch (error) {
      console.error("Failed to fetch track:", error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error("You can only edit your own tracks");
        navigate(`/track/${submission_id}`);
      } else {
        toast.error("Failed to load track");
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [submission_id, navigate]);

  useEffect(() => {
    fetchTrack();
  }, [submission_id, fetchTrack]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().replace(/^#/, '');
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 5) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // 🎭 Mood pills — fixed catalog so it stays curated and consistent
  // across the catalog (no free-text typos like "chillll").
  const MOOD_OPTIONS = [
    "chill", "hype", "moody", "uplifting", "romantic",
    "melancholy", "energetic", "smooth", "dark", "playful",
  ];
  const toggleMood = (m) => {
    setFormData(prev => {
      const has = prev.mood.includes(m);
      if (has) return { ...prev, mood: prev.mood.filter(x => x !== m) };
      if (prev.mood.length >= 4) return prev;  // cap at 4
      return { ...prev, mood: [...prev.mood, m] };
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type", { description: "Please upload a JPG or PNG image." });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large", { description: "Maximum file size is 5MB." });
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setCoverPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploadingCover(true);
    setCoverProgress(0);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('cover_art', file);

      const response = await api.post(`/track/${submission_id}/cover`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setCoverProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      });

      // Prefer the Cloudinary URL (permanent) returned by the server; fall back
      // to the local mirror with a cache-buster so the browser shows the new image.
      const finalUrl = response.data?.cover_url
        ? `${response.data.cover_url}?t=${Date.now()}`
        : null;
      if (finalUrl) setCoverPreview(finalUrl);
      toast.success("Cover art saved!");
      // Phase 3.2: explicit success flash so the user can see the save worked
      setCoverSavedFlash(true);
      setTimeout(() => setCoverSavedFlash(false), 2200);
    } catch (error) {
      console.error("Cover upload failed:", error);
      toast.error("Failed to upload cover art", { 
        description: error.response?.data?.detail || "Please try again" 
      });
      // Revert preview
      if (track?.cover_url) {
        setCoverPreview(track.cover_url);
      }
    } finally {
      setUploadingCover(false);
      setCoverProgress(0);
    }
  };

  // Phase 3.2: replace audio file
  const handleAudioChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/m4a', 'audio/x-m4a', 'audio/flac', 'audio/aac'];
    const validExt = ['.mp3', '.wav', '.m4a', '.flac', '.aac'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(file.type) && !validExt.includes(ext)) {
      toast.error("Invalid audio format", { description: "Use MP3, WAV, M4A, FLAC or AAC." });
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large", { description: "Maximum audio size is 50MB." });
      return;
    }

    setUploadingAudio(true);
    setAudioProgress(0);
    try {
      const fd = new FormData();
      fd.append('music_file', file);
      const response = await api.post(`/track/${submission_id}/audio`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setAudioProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      });
      toast.success("Audio file replaced!", { description: "New audio is now live on the track page." });
      setAudioSavedFlash(true);
      setTimeout(() => setAudioSavedFlash(false), 2400);
      // Update local track state with new music_url for any inline preview
      setTrack(prev => prev ? { ...prev, music_file: response.data.music_file, music_url: response.data.music_url } : prev);
    } catch (error) {
      console.error("Audio upload failed:", error);
      toast.error("Failed to upload audio", {
        description: error.response?.data?.detail || "Please try again",
      });
    } finally {
      setUploadingAudio(false);
      setAudioProgress(0);
      // Reset the file input so the same file can be re-selected if needed
      if (e.target) e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        track_title: formData.track_title,
        description: formData.description,
        genre: formData.genre,
        tags: formData.tags.length > 0 ? formData.tags : null,
        featured_artists: formData.featured_artists, // Phase 3.2
        // Phase 9.2 — fix-without-reupload polish
        mood: formData.mood.length > 0 ? formData.mood : null,
        credits: formData.credits || null,
        artist_name_display: formData.artist_name_display || null,
      };

      await api.patch(`/track/${submission_id}/edit`, updateData);
      toast.success("Track updated — your edits are saved.", {
        description: "Plays, gifts, comments, and rankings stayed untouched.",
      });
      navigate(`/track/${submission_id}`);
    } catch (error) {
      console.error("Track update failed:", error);
      toast.error("Failed to update track", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Loading track...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="track-edit-page">
      {/* Header */}
      <div className="border-b border-gray-800 bg-black/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <h1 className="text-2xl font-bold text-white" data-testid="track-edit-title">
              Edit Track
            </h1>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate(`/track/${submission_id}`)}
                variant="outline"
                className="text-white border-gray-700 hover:bg-gray-800"
              >
                View Track
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="text-white border-gray-700 hover:bg-gray-800"
              >
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Cover Art Preview */}
        <Card className="mb-8 overflow-hidden bg-gradient-to-br from-purple-900/30 to-black border-purple-500/30">
          <div className="p-6">
            <div className="flex items-start gap-6">
              {/* Cover Art Upload */}
              <div 
                className="relative cursor-pointer group"
                onClick={() => coverRef.current?.click()}
                data-testid="cover-upload-area"
              >
                <div className="w-40 h-40 rounded-xl overflow-hidden border-2 border-purple-500/50 bg-gray-900">
                  <img
                    src={coverPreview || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=200&fit=crop'}
                    alt="Cover Art"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=200&h=200&fit=crop'; }}
                  />
                </div>

                {/* Phase 3.2: Saved success flash */}
                {coverSavedFlash && !uploadingCover && (
                  <div className="absolute inset-0 rounded-xl bg-green-500/40 flex items-center justify-center pointer-events-none animate-pulse" data-testid="cover-saved-flash">
                    <div className="bg-black/70 rounded-full p-3">
                      <svg className="w-8 h-8 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
                
                {/* Upload overlay — always-visible during upload, hover-only otherwise */}
                <div className={`absolute inset-0 rounded-xl ${uploadingCover ? 'bg-black/70 opacity-100' : 'bg-black/50 opacity-0 group-hover:opacity-100'} transition-opacity flex flex-col items-center justify-center`}>
                  {uploadingCover ? (
                    <>
                      <svg className="animate-spin h-8 w-8 text-white mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <p className="text-white text-xs font-bold" data-testid="cover-progress">{coverProgress}%</p>
                      <div className="w-28 h-1 bg-gray-700 rounded-full overflow-hidden mt-1">
                        <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all" style={{ width: `${coverProgress}%` }}></div>
                      </div>
                    </>
                  ) : (
                    <div className="text-white text-center">
                      <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm">Change Cover</p>
                    </div>
                  )}
                </div>
                
                <input
                  ref={coverRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleCoverChange}
                  className="hidden"
                  data-testid="cover-input"
                />
              </div>

              <div className="flex-1 pt-2">
                <h2 className="text-2xl font-bold text-white mb-2">
                  {formData.track_title || 'Untitled Track'}
                </h2>
                <p className="text-purple-400 mb-2">{track?.artist_name}</p>
                <div className="flex items-center gap-4 text-gray-500 text-sm">
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {track?.play_count || 0} plays
                  </span>
                  {track?.score > 0 && (
                    <span className={`font-bold ${track.score >= 45 ? 'text-green-400' : track.score >= 30 ? 'text-yellow-400' : 'text-gray-400'}`}>
                      Score: {track.score}/60
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-xs mt-2">
                  Click on cover art to upload a new image
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Phase 3.2: Audio file replacement */}
        <Card className="mb-8 p-6" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <h3 className="text-white font-bold text-base mb-1">Audio File</h3>
              <p className="text-gray-400 text-sm mb-1">
                Replace the audio file without deleting the track.
                Plays, comments, and likes are preserved.
              </p>
              <p className="text-gray-500 text-xs">MP3, WAV, M4A, FLAC or AAC · max 50MB</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <label
                className={`px-4 py-2 rounded-lg text-white text-sm font-semibold cursor-pointer transition-all ${
                  uploadingAudio
                    ? 'bg-gray-700 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500'
                }`}
                data-testid="audio-replace-label"
              >
                {uploadingAudio ? `Uploading… ${audioProgress}%` : 'Replace Audio File'}
                <input
                  type="file"
                  accept=".mp3,.wav,.m4a,.flac,.aac,audio/*"
                  onChange={handleAudioChange}
                  disabled={uploadingAudio}
                  className="hidden"
                  data-testid="audio-input"
                />
              </label>
              {uploadingAudio && (
                <div className="w-48 h-1 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all" style={{ width: `${audioProgress}%` }}></div>
                </div>
              )}
              {audioSavedFlash && !uploadingAudio && (
                <span className="text-green-300 text-xs flex items-center gap-1" data-testid="audio-saved-flash">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved
                </span>
              )}
            </div>
          </div>
        </Card>

        {/* Edit Form */}
        <Card className="p-8" style={{ background: 'rgba(42, 26, 58, 0.4)', borderColor: 'rgba(138, 43, 226, 0.3)' }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Phase M1.5 — Allow this track to play in Beam Live sessions.
                Rendered only when the backend-advertised BEAM_MUSIC_ENABLED
                flag is ON. Keeps the toggle hidden in production until the
                Music Player is CEO-approved for public release. */}
            {beamMusicEnabled && (
            <div className="bg-black/40 border border-fuchsia-500/30 rounded-lg p-4"
                 data-testid="live-rebroadcast-consent-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <Label htmlFor="live-rebroadcast-toggle"
                         className="text-white font-semibold block">
                    🎵 Allow My Song in Beam Live
                  </Label>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                    Allow eligible RGM Studio Live hosts to play this song for
                    listeners during Beam Live sessions. You can turn this off
                    anytime. Songs listed for sale will only play their
                    available preview.
                  </p>
                </div>
                <button
                  id="live-rebroadcast-toggle"
                  type="button"
                  role="switch"
                  aria-checked={formData.live_rebroadcast_ok ? "true" : "false"}
                  data-testid="live-rebroadcast-toggle"
                  disabled={savingLiveRebroadcast}
                  onClick={async () => {
                    const next = !formData.live_rebroadcast_ok;
                    setSavingLiveRebroadcast(true);
                    try {
                      await api.post(
                        `/submissions/${submission_id}/music/consent`,
                        { live_rebroadcast_ok: next },
                      );
                      setFormData((prev) => ({ ...prev, live_rebroadcast_ok: next }));
                      toast.success(
                        next
                          ? "Allowed — hosts can now play this song in Beam Live"
                          : "Turned off — this song won't play in new Beam Live sessions",
                      );
                    } catch (e) {
                      toast.error(
                        e?.response?.data?.detail
                        || "Could not update your choice",
                      );
                    } finally {
                      setSavingLiveRebroadcast(false);
                    }
                  }}
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-colors disabled:opacity-50 ${
                    formData.live_rebroadcast_ok
                      ? "bg-fuchsia-500"
                      : "bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                      formData.live_rebroadcast_ok ? "translate-x-6" : "translate-x-1"
                    } mt-0.5`}
                  />
                </button>
              </div>
            </div>
            )}

            {/* Track Title */}
            <div>
              <Label htmlFor="track_title" className="text-gray-300">Track Title</Label>
              <Input
                id="track_title"
                value={formData.track_title}
                onChange={(e) => handleInputChange('track_title', e.target.value)}
                placeholder="Enter track title"
                className="mt-1 bg-black/50 border-gray-700 text-white"
                data-testid="input-track-title"
              />
            </div>

            {/* Genre */}
            <div>
              <Label htmlFor="genre" className="text-gray-300">Genre</Label>
              <Select
                value={formData.genre}
                onValueChange={(value) => handleInputChange('genre', value)}
              >
                <SelectTrigger 
                  className="mt-1 bg-black/50 border-gray-700 text-white"
                  data-testid="select-genre"
                >
                  <SelectValue placeholder="Select a genre" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {GENRES.map(genre => (
                    <SelectItem 
                      key={genre} 
                      value={genre}
                      className="text-white hover:bg-purple-600"
                    >
                      {genre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-gray-300">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="mt-1 bg-black/50 border-gray-700 text-white"
                placeholder="Tell listeners about this track..."
                data-testid="input-description"
              />
            </div>

            {/* Tags (Future-ready) */}
            <div>
              <Label htmlFor="tags" className="text-gray-300">
                Tags / Hashtags 
                <span className="text-gray-600 text-xs ml-2">(up to 5)</span>
              </Label>
              <div className="mt-1 flex gap-2">
                <Input
                  id="tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add tags (press Enter)"
                  className="bg-black/50 border-gray-700 text-white flex-1"
                  data-testid="input-tags"
                  disabled={formData.tags.length >= 5}
                />
                <Button
                  type="button"
                  onClick={handleAddTag}
                  variant="outline"
                  className="border-purple-500/50 text-purple-300"
                  disabled={!tagInput.trim() || formData.tags.length >= 5}
                >
                  Add
                </Button>
              </div>
              
              {/* Tag Display */}
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-purple-900/50 border border-purple-500/30 rounded-full text-purple-300 text-sm flex items-center gap-2"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-purple-400 hover:text-white"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Phase 9.2 — Mood pills */}
            <div>
              <Label className="text-gray-300">
                Mood / Vibe
                <span className="text-gray-600 text-xs ml-2">(up to 4 — pick what fits)</span>
              </Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="mood-options">
                {MOOD_OPTIONS.map((m) => {
                  const active = formData.mood.includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => toggleMood(m)}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        active
                          ? "bg-purple-500/40 border-purple-400 text-white"
                          : "bg-black/40 border-gray-700 text-gray-300 hover:border-purple-500/50"
                      }`}
                      data-testid={`mood-pill-${m}`}
                    >
                      {active ? "✓ " : ""}{m}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Phase 9.2 — Display artist name override */}
            <div>
              <Label htmlFor="artist_name_display" className="text-gray-300">
                Artist name on this track
                <span className="text-gray-600 text-xs ml-2">(optional — leave blank to use your profile name)</span>
              </Label>
              <Input
                id="artist_name_display"
                value={formData.artist_name_display}
                onChange={(e) => handleInputChange("artist_name_display", e.target.value)}
                placeholder="e.g. The Weeknd · alt-name only used on this release"
                className="bg-black/50 border-gray-700 text-white mt-1"
                data-testid="input-artist-name-display"
                maxLength={80}
              />
            </div>

            {/* Phase 9.2 — Credits / liner notes */}
            <div>
              <Label htmlFor="credits" className="text-gray-300">
                Credits / Producer info
                <span className="text-gray-600 text-xs ml-2">(producers, mixers, songwriters, samples)</span>
              </Label>
              <textarea
                id="credits"
                rows={3}
                value={formData.credits}
                onChange={(e) => handleInputChange("credits", e.target.value)}
                placeholder="Produced by … · Mixed by … · Samples …"
                className="w-full mt-1 px-3 py-2 bg-black/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                data-testid="input-credits"
                maxLength={1000}
              />
              <p className="text-xs text-gray-600 mt-1">{(formData.credits || "").length}/1000</p>
            </div>

            {/* Phase 3.2 / 15.6N: Collaborators */}
            <div>
              <Label className="text-gray-300">Collaborators</Label>
              <p className="text-xs text-gray-500 mb-2">Tag featured artists, producers, beat credits, or off-platform contributors. Newly-added RGM artists will be notified.</p>
              <CollabPicker
                value={formData.featured_artists || []}
                onChange={(next) => setFormData(prev => ({ ...prev, featured_artists: next }))}
                currentUserId={currentUserId}
              />
            </div>

            {/* Info Notice */}
            <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <p className="text-blue-300 text-sm flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  Your play count ({track?.play_count || 0}) and score ({track?.score || 0}/60) will be preserved when you save changes.
                </span>
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-800">
              <Button
                type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold"
                data-testid="save-track-btn"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
              <Button
                type="button"
                onClick={() => navigate(`/track/${submission_id}`)}
                variant="outline"
                className="text-white border-gray-700 hover:bg-gray-800"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default TrackEditPage;
